import React, { useState, useEffect } from 'react';
import './ModelInfo.css';

const FEATURE_LABELS = {
  // Stat differentials
  sig_strikes_per_min:              'Sig. strikes per minute',
  sig_absorbed_per_min:             'Sig. strikes absorbed per minute',
  sig_strike_differential_per_min:  'Striking differential per minute',
  total_strikes_per_min:            'Total strikes per minute',
  takedowns_per_min:                'Takedowns per minute',
  sub_attempts_per_min:             'Submission attempts per minute',
  knockdowns_per_min:               'Knockdowns per minute',
  control_pct:                      'Ground control %',
  sig_strike_accuracy:              'Sig. strike accuracy',
  takedown_accuracy:                'Takedown accuracy',
  head_strike_pct:                  'Head strike %',
  win_rate:                         'Win rate',
  finish_rate:                      'Finish rate',
  ko_rate:                          'KO/TKO rate',
  sub_rate:                         'Submission rate',
  decision_rate:                    'Decision rate',
  been_finished_rate:               'Has been finished rate',
  current_streak:                   'Current win/loss streak',
  last_3_win_rate:                  'Win rate (last 3 fights)',
  last_3_finish_rate:               'Finish rate (last 3 fights)',
  days_inactive:                    'Days inactive',
  fight_count:                      'Career fight count',
  avg_fight_duration_min:           'Avg fight duration (min)',
  // AdjPerf
  sig_strikes_per_min_adjperf_diff: 'Striking vs opponent quality',
  takedowns_per_min_adjperf_diff:   'Takedowns vs opponent quality',
  control_pct_adjperf_diff:         'Control % vs opponent quality',
  knockdowns_per_min_adjperf_diff:  'Knockdowns vs opponent quality',
  // Context
  elo_diff:                         'Elo rating gap',
  power_rating_diff:                'Power rating gap',
  striking_component_diff:          'Striking component gap',
  grappling_component_diff:         'Grappling component gap',
  durability_component_diff:        'Durability component gap',
  fight_iq_component_diff:          'Fight IQ component gap',
  reach_diff:                       'Reach gap (inches)',
  height_diff:                      'Height gap (inches)',
  age_diff:                         'Age gap (years)',
  avg_opponent_elo_diff:            'Avg opponent Elo diff (strength of schedule gap)',
  age_curve_a:                      'Age decline penalty (fighter A)',
  age_curve_b:                      'Age decline penalty (fighter B)',
  age_curve_diff:                   'Net age-curve advantage (A − B)',
  // Interactions
  elo_x_power:                      'Elo × power rating (combined dominance)',
  age_x_inactive_diff:              'Age × inactivity interaction',
  striking_vs_grappling:            'Striker vs grappler style clash',
};

const CATEGORY_META = {
  stat_differentials: {
    label: 'Per-minute stat differentials',
    desc: 'A − B differentials for rolling stats over the last 10 fights, normalised to per-minute rates and Bayesian-smoothed against UFC-wide priors.',
  },
  adjperf_differentials: {
    label: 'Opponent-adjusted performance',
    desc: 'AdjPerf = (observed − opponent_allows_median) / opponent_allows_MAD. Measures how a fighter performs relative to the quality of opposition they have faced.',
  },
  context: {
    label: 'Ratings & physical context',
    desc: 'Elo and power rating gaps, physical attributes (reach, height, age), and strength of schedule. Non-differenced — symmetric across both fighters.',
  },
  interactions: {
    label: 'Interaction terms',
    desc: 'Multiplicative combinations that capture non-linear relationships the model cannot learn from raw features alone.',
  },
};

function ModelInfo() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [featOpen, setFeatOpen] = useState({});
  const [promoting, setPromoting] = useState(false);

  useEffect(() => {
    fetch('/api/model/info', { credentials: 'include' })
      .then(r => r.json())
      .then(d => { setData(d); setLoading(false); })
      .catch(e => { setError(e.message); setLoading(false); });
  }, []);

  if (loading) return <div className="loading">Loading model info…</div>;
  if (error)   return <div className="mi-error">Failed to load: {error}</div>;

  const promoteCandidate = async () => {
    if (!window.confirm('Promote candidate model to production? This replaces the current model.')) return;
    setPromoting(true);
    try {
      const r = await fetch('/api/model/promote-candidate', {
        method: 'POST', credentials: 'include',
      });
      if (r.ok) {
        window.location.reload();
      }
    } catch (e) {
      console.error(e);
    }
    setPromoting(false);
  };

  const { active_model, gbm, candidate, walkforward, feature_categories, platt_params, settings } = data;

  const wfFull    = walkforward?.full_history;
  const wfRolling = walkforward?.rolling_window;
  const wfFolds   = wfFull?.folds || [];

  // Sort feature importance descending
  const importance = gbm?.feature_importance || {};
  const topFeatures = Object.entries(importance)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 15);
  const maxImp = topFeatures[0]?.[1] || 1;

  const fmtDate = iso => iso ? new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';
  const pct = v => v != null ? `${(v * 100).toFixed(1)}%` : '—';

  return (
    <div className="mi">
      <h2 className="mi-title">Model Information</h2>

      {/* ── Active model header ── */}
      <div className={`mi-header-card mi-header-${active_model}`}>
        <div className="mi-header-label">Active Model</div>
        <div className="mi-header-name">
          LightGBM (Gradient Boosted Trees)
        </div>
        <div className="mi-header-meta">
          {active_model === 'gbm' && gbm.trained && <>
            <span>{gbm.metrics?.n_features ?? gbm.feature_names?.length ?? '?'} features</span>
            <span>Trained {fmtDate(gbm.trained_at)}</span>
            {wfFull?.summary?.avg_accuracy && (
              <span className="mi-header-acc">{pct(wfFull.summary.avg_accuracy)} walk-forward accuracy</span>
            )}
          </>}
        </div>
      </div>

      {/* ── Prediction pipeline ── */}
      <section className="mi-section">
        <h3 className="mi-section-title">Prediction Model</h3>
        <div className="mi-pipeline">
          <div className={`mi-pipeline-step ${gbm.trained ? 'mi-pipeline-active' : 'mi-pipeline-missing'}`}>
            <div className="mi-pipeline-num">1</div>
            <div>
              <div className="mi-pipeline-name">LightGBM (GBM)</div>
              <div className="mi-pipeline-desc">45 features · Bayesian smoothing · AdjPerf · Platt calibration</div>
            </div>
            <div className="mi-pipeline-badge">
              {gbm.trained ? 'trained' : 'not trained'}
            </div>
          </div>
        </div>
      </section>

      {/* ── Candidate model ── */}
      {candidate && (
        <section className="mi-section">
          <h3 className="mi-section-title">Candidate Model</h3>
          <div className="mi-pipeline">
            <div className="mi-pipeline-step mi-pipeline-active" style={{ borderColor: '#f59e0b' }}>
              <div className="mi-pipeline-num" style={{ background: '#f59e0b' }}>C</div>
              <div>
                <div className="mi-pipeline-name">Auto-Retrained Model</div>
                <div className="mi-pipeline-desc">
                  Trained {fmtDate(candidate.trained_at)}
                  {candidate.metrics?.train?.accuracy && ` · ${pct(candidate.metrics.train.accuracy)} train accuracy`}
                  {candidate.metrics?.test?.brier_score && ` · ${candidate.metrics.test.brier_score.toFixed(4)} Brier`}
                </div>
              </div>
              <button
                className="mi-pipeline-badge"
                style={{ cursor: 'pointer', background: '#f59e0b', color: '#000', border: 'none', padding: '4px 12px', borderRadius: 4 }}
                onClick={promoteCandidate}
                disabled={promoting}
              >
                {promoting ? 'Promoting...' : 'Promote'}
              </button>
            </div>
          </div>
        </section>
      )}

      {/* ── Walk-forward accuracy ── */}
      {wfFolds.length > 0 && (
        <section className="mi-section">
          <h3 className="mi-section-title">Walk-Forward Validation</h3>
          <p className="mi-prose">
            The model is never tested on data it was trained on. Each fold trains on all fights <em>before</em> the test year and evaluates on fights in that year only — replicating real deployment conditions.
            Two variants are compared: <strong>full history</strong> (train on all data since 2015) and a <strong>{walkforward?.rolling_window?.folds?.[0] ? `${walkforward.window_years ?? 5}-year rolling window`  : '5-year rolling window'}</strong>.
          </p>
          <div className="mi-table-wrap">
            <table className="mi-table">
              <thead>
                <tr>
                  <th>Year</th>
                  <th>Test fights</th>
                  <th>Full history</th>
                  <th>Rolling window</th>
                  <th>Brier score</th>
                  <th>Winner</th>
                </tr>
              </thead>
              <tbody>
                {wfFolds.map((fold, i) => {
                  const roll = wfRolling?.folds?.[i];
                  const fullAcc = fold.accuracy;
                  const rollAcc = roll && !roll.error ? roll.accuracy : null;
                  const fullWins = rollAcc === null || fullAcc >= rollAcc;
                  return (
                    <tr key={fold.test_year}>
                      <td className="mi-td-year">{fold.test_year}</td>
                      <td className="mi-td-dim">{fold.test_n?.toLocaleString()}</td>
                      <td className={fullWins ? 'mi-td-winner' : 'mi-td-dim'}>{pct(fullAcc)}</td>
                      <td className={!fullWins ? 'mi-td-winner' : 'mi-td-dim'}>{rollAcc != null ? pct(rollAcc) : '—'}</td>
                      <td className="mi-td-dim">{fold.brier?.toFixed(4)}</td>
                      <td className="mi-td-label">{fullWins ? 'Full' : `Rolling`}</td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="mi-tr-avg">
                  <td>Average</td>
                  <td className="mi-td-dim">{wfFolds.reduce((s, f) => s + (f.test_n || 0), 0).toLocaleString()} total</td>
                  <td className={(wfFull?.summary?.avg_accuracy || 0) >= (wfRolling?.summary?.avg_accuracy || 0) ? 'mi-td-winner' : 'mi-td-dim'}>
                    {pct(wfFull?.summary?.avg_accuracy)}
                  </td>
                  <td className={(wfRolling?.summary?.avg_accuracy || 0) > (wfFull?.summary?.avg_accuracy || 0) ? 'mi-td-winner' : 'mi-td-dim'}>
                    {pct(wfRolling?.summary?.avg_accuracy)}
                  </td>
                  <td className="mi-td-dim">{wfFull?.summary?.avg_brier?.toFixed(4)}</td>
                  <td className="mi-td-label">
                    {(wfFull?.summary?.avg_accuracy || 0) >= (wfRolling?.summary?.avg_accuracy || 0) ? '✓ Full wins' : '✓ Rolling wins'}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
          <p className="mi-table-note">
            Brier score measures probability calibration — lower is better (0 is perfect, 0.25 is random). A Brier score of ~0.12 indicates well-calibrated probabilities.
          </p>
        </section>
      )}

      {/* ── Feature importance ── */}
      {topFeatures.length > 0 && (
        <section className="mi-section">
          <h3 className="mi-section-title">Top Features by Importance</h3>
          <p className="mi-prose">
            LightGBM feature importance (gain) — how much each feature contributes to reducing prediction error across all trees. All features are fighter A minus fighter B differentials.
          </p>
          <div className="mi-importance-list">
            {topFeatures.map(([name, imp]) => (
              <div key={name} className="mi-importance-row">
                <div className="mi-importance-label">{FEATURE_LABELS[name] || name}</div>
                <div className="mi-importance-bar-wrap">
                  <div className="mi-importance-bar" style={{ width: `${(imp / maxImp * 100).toFixed(1)}%` }} />
                </div>
                <div className="mi-importance-val">{imp.toFixed(1)}</div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* ── Feature categories ── */}
      {Object.keys(feature_categories || {}).length > 0 && (
        <section className="mi-section">
          <h3 className="mi-section-title">
            All Features ({Object.values(feature_categories).flat().length} total)
          </h3>
          <div className="mi-feat-grid">
            {Object.entries(CATEGORY_META).map(([key, meta]) => {
              const feats = feature_categories[key] || [];
              const open = featOpen[key];
              return (
                <div key={key} className="mi-feat-card">
                  <button className="mi-feat-header" onClick={() => setFeatOpen(p => ({ ...p, [key]: !p[key] }))}>
                    <div>
                      <div className="mi-feat-title">{meta.label}</div>
                      <div className="mi-feat-count">{feats.length} feature{feats.length !== 1 ? 's' : ''}</div>
                    </div>
                    <span className="mi-feat-chevron">{open ? '▲' : '▼'}</span>
                  </button>
                  <p className="mi-feat-desc">{meta.desc}</p>
                  {open && (
                    <ul className="mi-feat-list">
                      {feats.map(f => (
                        <li key={f}>
                          <span className="mi-feat-raw">{f}</span>
                          {FEATURE_LABELS[f] && <span className="mi-feat-human"> — {FEATURE_LABELS[f]}</span>}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* ── How probabilities are built ── */}
      <section className="mi-section">
        <h3 className="mi-section-title">How Win Probabilities Are Calculated</h3>
        <div className="mi-pipeline-text">
          <div className="mi-step-block">
            <div className="mi-step-n">1</div>
            <div>
              <div className="mi-step-title">Raw features</div>
              <div className="mi-step-body">
                For each fighter: rolling stats over the last 10 fights (strictly before fight date, no look-ahead). Per-minute rates are computed using total fight time. Bayesian smoothing pulls estimates toward UFC-wide priors when sample sizes are small.
              </div>
            </div>
          </div>
          <div className="mi-step-block">
            <div className="mi-step-n">2</div>
            <div>
              <div className="mi-step-title">Opponent adjustment (AdjPerf)</div>
              <div className="mi-step-body">
                Each stat is normalised against the median and MAD of what opponents typically allow. A fighter who lands 5 takedowns/min against wrestlers is credited more than one who does it against poor wrestlers.
              </div>
            </div>
          </div>
          <div className="mi-step-block">
            <div className="mi-step-n">3</div>
            <div>
              <div className="mi-step-title">GBM prediction</div>
              <div className="mi-step-body">
                A LightGBM model trained on ~{wfFolds[wfFolds.length - 1]?.train_n?.toLocaleString() ?? '8,000+'} historical fights outputs a raw win probability for fighter A. The symmetric difference vector (A−B) means the model is naturally symmetric — swapping fighters flips the probability.
              </div>
            </div>
          </div>
          <div className="mi-step-block">
            <div className="mi-step-n">4</div>
            <div>
              <div className="mi-step-title">Platt calibration</div>
              <div className="mi-step-body">
                A logistic calibration layer (Platt scaling) corrects systematic over/under-confidence in the raw model output.
                {platt_params && <> Current parameters: A = {platt_params.A?.toFixed(3)}, B = {platt_params.B?.toFixed(3)} (fit on {platt_params.n_samples?.toLocaleString()} samples).</>}
              </div>
            </div>
          </div>
          <div className="mi-step-block">
            <div className="mi-step-n">5</div>
            <div>
              <div className="mi-step-title">Fight adjustments</div>
              <div className="mi-step-body">
                Context modifiers are applied on top of the model probability: layoff penalty (long inactivity), weight cut penalty, short-notice penalty (fight announced &lt;14 days out), style matchup bonus/penalty, and momentum modifier (±3% for streak).
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Power rating components ── */}
      <section className="mi-section">
        <h3 className="mi-section-title">Power Rating Components</h3>
        <p className="mi-prose">
          Each fighter's power rating is a weighted composite of four domain scores (each 0–100), used as features in the GBM model and as a standalone fallback.
        </p>
        <div className="mi-component-grid">
          {[
            {
              name: 'Striking', weight: '35%',
              desc: 'Significant strikes landed per 15 min, strike accuracy, head strike %, knockdown rate.',
            },
            {
              name: 'Grappling', weight: '30%',
              desc: 'Takedowns per 15 min, takedown accuracy, submission attempts per 15 min, ground control time.',
            },
            {
              name: 'Durability', weight: '20%',
              desc: 'Inverse of strikes absorbed per 15 min, takedown defence %, submission defence, and been-finished rate.',
            },
            {
              name: 'Fight IQ', weight: '15%',
              desc: (
                <>
                  A composite of decision literacy and experience:
                  <ul className="mi-iq-list">
                    <li><b>25%</b> Decision win rate — winning by outpointing shows ring generalship</li>
                    <li><b>20%</b> Finish rate — finishing fights shows dominance and adaptability</li>
                    <li><b>20%</b> Fight pace — sustained output and activity level</li>
                    <li><b>20%</b> Championship rounds (R4/R5) experience — proven under pressure</li>
                    <li><b>15%</b> Total UFC fights — seasoned campaigners rated higher</li>
                  </ul>
                  Score 0–100; defaults to 50 for fighters with insufficient data.
                </>
              ),
            },
          ].map(c => (
            <div key={c.name} className="mi-component-card">
              <div className="mi-component-head">
                <span className="mi-component-name">{c.name}</span>
                <span className="mi-component-weight">{c.weight} of power rating</span>
              </div>
              <div className="mi-component-desc">{c.desc}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ── Style classification ── */}
      <section className="mi-section">
        <h3 className="mi-section-title">Fighting Style Classification</h3>
        <p className="mi-prose">
          Each fighter is assigned one of five styles based on their stats from <code>fighter_stats_aggregated</code>. Style is used to adjust age penalties (strikers decline earlier, grapplers later) and to generate matchup analysis in the betting reasoning.
        </p>
        <div className="mi-component-grid">
          {[
            {
              name: 'Wrestler',
              desc: 'Takedowns per 15 min > 3.0 AND takedown accuracy > 50% AND positive control time. Prioritised first — pure TD volume + accuracy + control defines the archetype.',
            },
            {
              name: 'Submission Grappler',
              desc: 'Submission attempts per 15 min > 0.5. Checked after wrestler — high sub attempt rate regardless of TD volume.',
            },
            {
              name: 'Striker',
              desc: 'Striking composite score (sig strikes/min × 0.5 + strike accuracy × 50) is more than 2× the grappling composite score. Volume + accuracy dominates.',
            },
            {
              name: 'Counter Striker',
              desc: 'Low volume (sig strikes/min < 5.0) but high knockout rate (> 10%) OR high strike accuracy (> 55%). Picks spots rather than brawls.',
            },
            {
              name: 'Balanced',
              desc: 'Does not clearly fit any of the above. Well-rounded or insufficient data to classify.',
            },
          ].map(c => (
            <div key={c.name} className="mi-component-card">
              <div className="mi-component-head">
                <span className="mi-component-name">{c.name}</span>
              </div>
              <div className="mi-component-desc">{c.desc}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ── Bet sizing ── */}
      <section className="mi-section">
        <h3 className="mi-section-title">Bet Sizing</h3>
        <div className="mi-pipeline-text">
          <div className="mi-step-block">
            <div className="mi-step-n">1</div>
            <div>
              <div className="mi-step-title">Edge calculation</div>
              <div className="mi-step-body">
                Edge = model probability − implied probability from the best available bookmaker odds. Only bets with edge ≥ {pct(settings?.min_edge)} are surfaced.
              </div>
            </div>
          </div>
          <div className="mi-step-block">
            <div className="mi-step-n">2</div>
            <div>
              <div className="mi-step-title">Kelly Criterion</div>
              <div className="mi-step-body">
                Kelly fraction = edge / (decimal odds − 1). Scaled by a fixed fraction ({pct(settings?.kelly_fraction)}) to reduce variance. Additionally scaled by a confidence factor (0.30–1.00) based on how many fights the model has seen for each fighter — fighters with fewer than 3 fights get 30% Kelly.
              </div>
            </div>
          </div>
          <div className="mi-step-block">
            <div className="mi-step-n">3</div>
            <div>
              <div className="mi-step-title">Portfolio adjustment</div>
              <div className="mi-step-body">
                When multiple bets are placed on the same event, individual Kelly sizes are divided by √N (where N = number of correlated bets) to account for the fact that same-event outcomes are correlated and simultaneously compressing the bankroll.
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

export default ModelInfo;
